import React from 'react';
import {
  Typography,
  Box,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  Collapse,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Language as LanguageIcon,
  PanTool as HandsUpIcon,
  People as PersonsIcon,
  AccountBox as AccountIcon,
} from '@mui/icons-material';
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import _ from 'lodash';
import { colors, typography } from '../styles/globals.js';
import { Colors } from '../styles/colors';
import KeyboardArrowRighIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { TextField, ClickAwayListener } from '@mui/material';
import { useAuth } from '../auth/AuthContext.js';
import { useEditMode } from '../components/EditMode/EditModeContext';
import { RISK_SCORE_MAP } from '../components/EditMode/RiskEditControls';
import axios from '../auth/axiosConfig';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:8010/v1';

// ADD: a tiny circular icon helper
const ErrorRoundIcon = ({ size = 18, iconSize = 12, bg = Colors.ERROR_RED }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      bgcolor: bg,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: '0 0 auto',
    }}
  >
    <PriorityHighRoundedIcon sx={{ fontSize: iconSize, color: Colors.WHITE }} />
  </Box>
);

const SuccessRoundIcon = ({ size = 18, iconSize = 14, color = Colors.SUCCESS_GREEN }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      bgcolor: color,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: '0 0 auto',
    }}
  >
    <DoneRoundedIcon sx={{ fontSize: iconSize, color: Colors.WHITE }} />
  </Box>
);

// Convert score type to display name
const scoreTypeMap = {
  TRIVIAL: 'Overall risk',
  KYB: 'KYB risk',
  FIRST_PARTY: 'First party risk',
  SYNTHETIC: 'Synthetic risk',
  THIRD_PARTY: 'Third party risk',
};

const getScoreTypeName = (scoreType, tenant) => {
  if (!scoreType) return '';
  console.log('tenant', tenant);

  if (tenant?.name == 'lead-production' && scoreType == 'KYB') {
    return 'KYB risk';
  }

  return (
    scoreTypeMap[scoreType] ||
    _.chain(scoreType)
      .split(/(?=[A-Z])|_/)
      .map((word) => _.capitalize(word.toLowerCase()))
      .join(' ')
      .value()
  );
};

const getScoreTypeIcon = (scoreType) => {
  const baseIconProps = {
    color: colors.white,
    borderRadius: '8px',
    padding: '8px',
    fontSize: '35px',
  };

  switch (scoreType) {
    case 'TRIVIAL': // Overall risk
      return (
        <LanguageIcon sx={{ ...baseIconProps, backgroundColor: Colors.BRAND_BLUE }} />
      );
    case 'KYB': // KYB risk (Risk Mitigator)
      return (
        <AccountIcon sx={{ ...baseIconProps, backgroundColor: Colors.KYB_PURPLE }} />
      );
    case 'FIRST_PARTY': // First party fraud risk
      return (
        <HandsUpIcon sx={{ ...baseIconProps, backgroundColor: Colors.FIRST_PARTY_ORANGE }} />
      );
    case 'SYNTHETIC': // Synthetic fraud risk
      return (
        <PersonsIcon sx={{ ...baseIconProps, backgroundColor: Colors.SYNTHETIC_BLACK }} />
      );
    default:
      return (
        <LanguageIcon sx={{ ...baseIconProps, backgroundColor: Colors.BRAND_BLUE }} />
      );
  }
};

// Convert chip labels to sentence case
const formatChipLabel = (label) => {
  if (!label) return '';
  return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
};

// General function to map UI strings to tab IDs and indices
const getTabMapping = (item) => {
  if (!item || !item.ui_string) return null;

  const uiString = item.ui_string.toLowerCase();
  console.log({ uiString });

  const mappings = [
    // Order matters - more specific patterns first
    {
      pattern: 'business owner',
      tabIndex: 0,
      elementId: 'overview-tab-profile',
    },
    { pattern: 'email address', tabIndex: 7, elementId: 'email-tab-profile' },
    { pattern: 'email', tabIndex: 7, elementId: 'email-tab-profile' },
    {
      pattern: 'entity website',
      tabIndex: 3,
      elementId: 'main-website-tab-profile',
    },
    {
      pattern: 'entity registration',
      tabIndex: 0,
      elementId: 'overview-tab-profile',
    },
    {
      pattern: 'entity operating activity',
      tabIndex: 4,
      elementId: 'web-presence-tab-profile',
    },
    {
      pattern: 'official website',
      tabIndex: 3,
      elementId: 'main-website-tab-profile',
    },
    { pattern: 'website', tabIndex: 3, elementId: 'main-website-tab-profile' },
    { pattern: 'web presence', tabIndex: 4, elementId: null },
    { pattern: 'watchlists', tabIndex: 1, elementId: 'associated-tab-profile' },
    { pattern: 'address', tabIndex: 2, elementId: 'address-tab-profile' },
    { pattern: 'phone', tabIndex: 8, elementId: 'phone-tab-profile' },
    { pattern: 'telephone', tabIndex: 8, elementId: 'phone-tab-profile' },
    { pattern: 'litigation', tabIndex: 6, elementId: 'litigations-tab-profile' },
    { pattern: 'lien', tabIndex: 5, elementId: 'liens-tab-profile' },
  ];

  // Find matching mapping - check more specific patterns first
  for (const mapping of mappings) {
    if (uiString.includes(mapping.pattern)) {
      return { tabIndex: mapping.tabIndex, elementId: mapping.elementId };
    }
  }

  return null;
};

// Get UI configs based on assessment risk level
const getRiskLevelConfig = (risk_level) => {
  switch (risk_level) {
    case 'LOW':
      return {
        level: 'LOW RISK',
        color: Colors.SUCCESS_GREEN,
        backgroundColor: Colors.LOW_RISK_BG,
        icon: <CheckCircleIcon fontSize="small" />,
      };
    case 'BORDERLINE':
      return {
        level: 'BORDERLINE',
        color: Colors.BORDERLINE_YELLOW,
        backgroundColor: Colors.MODERATE_RISK_BG,
        icon: <ErrorIcon fontSize="small" />,
      };
    case 'MODERATE':
      return {
        level: 'MODERATE RISK',
        color: Colors.WARNING_ORANGE,
        backgroundColor: Colors.MODERATE_RISK_BG,
        icon: <ErrorIcon fontSize="small" />,
      };
    case 'HIGH':
      return {
        level: 'HIGH RISK',
        color: Colors.ERROR_RED,
        backgroundColor: Colors.HIGH_RISK_BG,
        icon: <ErrorIcon fontSize="small" />,
      };
    default:
      return {
        level: risk_level,
        color: '#9E9E9E',
        icon: <ErrorIcon fontSize="small" />,
      };
  }
};

const getUniqueAssessments = (assessments) => {
  return _.uniqBy(assessments, 'ui_string').filter(
    (item) => item.ui_string !== 'None'
  );
};

const formatUIString = (ui_string) => {
  if (!ui_string) return '';

  // Check if string contains a dash
  if (ui_string.includes(' - ')) {
    const [beforeDash, afterDash] = ui_string.split(' - ', 2);
    return (
      <>
        <span style={{ fontWeight: 500 }}>{beforeDash}</span> - {afterDash}
      </>
    );
  } else {
    // No dash - make first two words semi-bold
    const words = ui_string.split(' ');
    if (words.length <= 2) {
      return <span style={{ fontWeight: 500 }}>{ui_string}</span>;
    } else {
      const firstTwoWords = words.slice(0, 2).join(' ');
      const remainingWords = words.slice(2).join(' ');
      return (
        <>
          <span style={{ fontWeight: 500 }}>{firstTwoWords}</span>{' '}
          {remainingWords}
        </>
      );
    }
  }
};

const EditableAssessmentText = ({ item, scoreType, listType, riskSummary, onUpdateRiskSummary }) => {
  const { isEditMode, addChangeDescription } = useEditMode();
  const [isEditing, setIsEditing] = React.useState(false);
  const [editValue, setEditValue] = React.useState(item.ui_string);

  if (!isEditMode || !onUpdateRiskSummary) {
    return formatUIString(item.ui_string);
  }

  if (isEditing) {
    return (
      <ClickAwayListener onClickAway={() => {
        if (editValue !== item.ui_string) {
          const listKey = listType === 'pos' ? 'top_pos_assessments' : 'top_neg_assessments';
          const updated = riskSummary.map(s => {
            if (s.score_type !== scoreType) return s;
            return {
              ...s,
              [listKey]: s[listKey].map(a =>
                a.ui_string === item.ui_string ? { ...a, ui_string: editValue } : a
              ),
            };
          });
          addChangeDescription(`Edited assessment: "${editValue}"`);
          onUpdateRiskSummary(updated);
        }
        setIsEditing(false);
      }}>
        <TextField
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          size="small"
          fullWidth
          autoFocus
          multiline
          variant="standard"
          sx={{ fontSize: typography.fontSizes.small }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              e.target.blur();
            }
            if (e.key === 'Escape') {
              setEditValue(item.ui_string);
              setIsEditing(false);
            }
          }}
        />
      </ClickAwayListener>
    );
  }

  return (
    <span
      onClick={(e) => { e.stopPropagation(); setEditValue(item.ui_string); setIsEditing(true); }}
      style={{ cursor: 'pointer', borderBottom: '1px dashed #4566EB' }}
      title="Click to edit"
    >
      {formatUIString(item.ui_string)}
    </span>
  );
};

const RiskSummaryDetailView = ({
  risk_summary,
  title,
  onNavigateToTab,
  verificationId,
  onUpdateRiskSummary,
}) => {
  // Initialize warnings as expanded and mitigators as collapsed by default

  const initializeExpandedStates = () => {
    const initialStates = {};
    if (risk_summary && Array.isArray(risk_summary)) {
      risk_summary
        .filter(assessment => assessment.score_type !== 'THIRD_PARTY')
        .forEach((assessment, index) => {
          const cardId = `card-${index}`;
          initialStates[`${cardId}-warnings`] = true;
          initialStates[`${cardId}-mitigators`] = false;
        });
    }
    return initialStates;
  };

  const { tenant } = useAuth();
  const { isEditMode } = useEditMode();
  const [expandedStates, setExpandedStates] = React.useState(
    initializeExpandedStates()
  );

  const [anchorEls, setAnchorEls] = React.useState({});
  const [feedbackStatus, setFeedbackStatus] = React.useState({});
  const [pageStartTime] = React.useState(() => Date.now());
  const [updatedRiskLevels, setUpdatedRiskLevels] = React.useState({});
  const [submittingFeedback, setSubmittingFeedback] = React.useState({});

  const isScoringFeedbackEnabled = tenant?.scoring_feedback_enabled === true;

  // Helper function to get time spent on page
  const getTimeSpentOnPage = () => {
    return Date.now() - pageStartTime;
  };

  // Function to send scoring feedback to API
  const sendScoringFeedback = async (
    assessment,
    timeSpentInSeconds,
    timeSpentMs,
    newRiskLevel,
    cardId
  ) => {
    // Set submitting state to prevent multiple submissions
    setSubmittingFeedback((prev) => ({
      ...prev,
      [cardId]: true,
    }));

    try {
      const response = await axios.post(
        '/ui/scoring-feedback',
        {
          verification_id: verificationId,
          score_type: assessment.score_type,
          original_risk_level: assessment.risk_level,
          new_risk_level: newRiskLevel,
          assessment_data: assessment,
          time_spent_on_page_seconds: timeSpentInSeconds,
          time_spent_on_page_ms: timeSpentMs,
        },
      );

      const result = response.data;
      console.log('Scoring feedback saved successfully:', result);

      // Sync risk_level to parent data model so edit mode snapshots are current.
      // Uses functional updater to avoid stale closure if multiple feedbacks are in flight.
      if (onUpdateRiskSummary) {
        onUpdateRiskSummary(prev => prev.map(s => {
          if (s.score_type !== assessment.score_type) return s;
          return { ...s, risk_level: newRiskLevel, score: RISK_SCORE_MAP[newRiskLevel] ?? s.score };
        }));
      }

      setUpdatedRiskLevels((prev) => ({
        ...prev,
        [cardId]: newRiskLevel,
      }));

      // Set success state
      setFeedbackStatus((prev) => ({
        ...prev,
        [cardId]: { status: 'success', message: 'Score feedback submitted' }
      }));

      // Auto-dismiss after 3 seconds
      setTimeout(() => {
        setFeedbackStatus((prev) => {
          const newState = { ...prev };
          delete newState[cardId];
          return newState;
        });
      }, 3000);
    } catch (error) {
      console.error('Error sending scoring feedback:', error);

      // Set error state
      setFeedbackStatus((prev) => ({
        ...prev,
        [cardId]: {
          status: 'error',
          message: 'Failed to save scoring feedback. Please try again.',
        },
      }));

      // Auto-dismiss after 5 seconds
      setTimeout(() => {
        setFeedbackStatus((prev) => {
          const newState = { ...prev };
          delete newState[cardId];
          return newState;
        });
      }, 5000);
    } finally {
      // Always clear the submitting state
      setSubmittingFeedback((prev) => {
        const newState = { ...prev };
        delete newState[cardId];
        return newState;
      });
    }
  };

  // Helper function to toggle the expanded state of a section
  const toggleExpanded = (cardId, section) => {
    const key = `${cardId}-${section}`;

    setExpandedStates((prev) => {
      const newValue = !prev[key];

      const newState = {
        ...prev,
        [key]: newValue,
      };

      return newState;
    });
  };

  // Dropdown handlers
  const handleDropdownOpen = (event, cardId) => {
    // Prevent opening if currently submitting feedback
    if (submittingFeedback[cardId]) {
      return;
    }

    setAnchorEls((prev) => ({
      ...prev,
      [cardId]: event.currentTarget,
    }));
  };

  const handleDropdownClose = (cardId) => {
    setAnchorEls((prev) => {
      const newState = { ...prev };
      delete newState[cardId]; // Remove the key instead of setting to null
      return newState;
    });
  };

  const handleRiskLevelSelect = (newRiskLevel, assessment, index) => {
    if (!isScoringFeedbackEnabled) return;

    const cardId = `card-${index}`;

    // Prevent multiple submissions
    if (submittingFeedback[cardId]) {
      return;
    }

    // Calculate time spent on page
    const timeSpent = getTimeSpentOnPage();
    const timeSpentInSeconds = Math.round(timeSpent / 1000); // Convert to seconds

    console.log('Risk level changed:', {
      newRiskLevel,
      assessment,
      index,
      timeSpentOnPage: timeSpentInSeconds,
      timeSpentOnPageMs: timeSpent,
    });

    // Close the dropdown first
    handleDropdownClose(cardId);

    // Send scoring feedback to server
    sendScoringFeedback(
      assessment,
      timeSpentInSeconds,
      timeSpent,
      newRiskLevel,
      cardId
    );
  };

  if (
    !risk_summary ||
    !Array.isArray(risk_summary) ||
    risk_summary.length === 0
  ) {
    return <Typography>No scoring data available</Typography>;
  }

  return (
    <Paper sx={{ p: 2, mb: 2, borderRadius: 2, border: `1px solid ${Colors.BORDER_GRAY}` }}>
      {title && (
        <Typography
          sx={{
            fontWeight: typography.fontWeights.semibold,
            fontSize: typography.fontSizes.h3,
            mt: 1,
          }}
        >
          {title}
        </Typography>
      )}
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        {_.map(risk_summary, (assessment, index) => {
          const cardId = `card-${index}`;
          const currentRiskLevel =
            updatedRiskLevels[cardId] || assessment.risk_level;
          const riskLevel = getRiskLevelConfig(currentRiskLevel);
          const uniquePosAssessments = getUniqueAssessments(assessment.top_pos_assessments || []);
          const uniqueNegAssessments = getUniqueAssessments(assessment.top_neg_assessments || []);
          return (
            <Grid item xs={12} md={6} lg={3} key={index}>
              <Paper
                elevation={2}
                sx={{
                  height: 'auto',
                  borderRadius: 2,
                  overflow: 'visible',
                  backgroundColor: riskLevel.backgroundColor,
                  position: 'relative',
                  '&:hover': {
                    boxShadow: 3,
                  },
                }}
              >
                {/* Feedback Alert */}
                {feedbackStatus[cardId] && (
                  <div
                    style={{
                      opacity: feedbackStatus[cardId] ? 1 : 0,
                      transition: 'opacity 0.3s ease-in-out',
                      position: 'absolute',
                      top: '-35px',
                      right: '-10px',
                      zIndex: 10,
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                      padding: '6px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: '#000000',
                      color: '#ffffff',
                      border: '1px solid #000000',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <div>
                      {feedbackStatus[cardId].message}
                    </div>
                  </div>
                )}
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}
                >
                  {/* Icon and Title Row */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    {getScoreTypeIcon(assessment.score_type)}
                    <Typography
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {getScoreTypeName(assessment.score_type, tenant)}
                    </Typography>
                  </Box>

                  {/* Risk Level Chip Row */}
                  <Box>
                    <Chip
                      label={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          {formatChipLabel(riskLevel.level)}
                          {isScoringFeedbackEnabled && !isEditMode && <ExpandMoreIcon />}
                        </Box>
                      }
                      icon={riskLevel.icon}
                      onClick={
                        isScoringFeedbackEnabled && !isEditMode
                          ? (event) => handleDropdownOpen(event, cardId)
                          : undefined
                      }
                      size="medium"
                      sx={{
                        backgroundColor: `${riskLevel.color}`,
                        color: riskLevel.backgroundColor,
                        fontWeight: 'bold',
                        height: '28px',
                        fontSize: '0.875rem',
                        cursor: isScoringFeedbackEnabled && !isEditMode
                          ? 'pointer'
                          : 'default',
                        '&:hover': {
                          backgroundColor: `${riskLevel.color}`,
                        },
                        '& .MuiChip-icon': {
                          color: riskLevel.backgroundColor,
                        },
                        '& .MuiChip-deleteIcon': {
                          color: riskLevel.backgroundColor,
                          fontSize: '18px',
                          '&:hover': {
                            color: riskLevel.backgroundColor,
                          },
                        },
                      }}
                    />

                    {/* Dropdown Menu */}
                    {isScoringFeedbackEnabled && (
                      <Menu
                        anchorEl={anchorEls[cardId]}
                        open={Boolean(anchorEls[cardId])}
                        onClose={() => handleDropdownClose(cardId)}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'left',
                        }}
                      >
                        {_.map(['LOW', 'MODERATE', 'HIGH'], (riskType) => {
                          const typeConfig = getRiskLevelConfig(riskType);
                          return (
                            <MenuItem
                              key={riskType}
                              onClick={() =>
                                handleRiskLevelSelect(
                                  riskType,
                                  assessment,
                                  index
                                )
                              }
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                minWidth: '120px',
                              }}
                            >
                              <Typography
                                sx={{
                                  color: typeConfig.color,
                                  fontWeight: 'bold',
                                  fontSize: '0.875rem',
                                }}
                              >
                                {formatChipLabel(typeConfig.level)}
                              </Typography>
                            </MenuItem>
                          );
                        })}
                      </Menu>
                    )}
                  </Box>
                </Box>

                <Box py={1} px={2}>
                  {/* Risk Warnings */}
                  <Box sx={{ mb: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 1,
                        cursor:
                          uniquePosAssessments.length > 0
                            ? 'pointer'
                            : 'default',
                        '&:hover':
                          uniquePosAssessments.length > 0
                            ? {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            }
                            : {},
                        p: 1,
                        borderRadius: 1,
                        gap: 1,
                      }}
                      onClick={
                        uniquePosAssessments.length > 0
                          ? () => toggleExpanded(cardId, 'warnings')
                          : undefined
                      }
                    >
                      <ErrorRoundIcon size={16} iconSize={10} />
                      <Typography
                        sx={{
                          color: colors.primary,
                          fontWeight: typography.fontWeights.regular,
                          fontSize: typography.fontSizes.medium,
                          textTransform: 'none',
                          letterSpacing: '0.5px',
                        }}
                      >
                        {`${uniquePosAssessments.length} Risk ${uniquePosAssessments.length === 1
                          ? 'warning'
                          : 'warnings'
                          }`}
                      </Typography>
                      <Box sx={{ flex: 1 }} />

                      {uniquePosAssessments.length > 0 &&
                        (expandedStates[`${cardId}-warnings`] ? (
                          <KeyboardArrowDownIcon
                            fontSize="medium"
                            sx={{ color: colors.primary }}
                          />
                        ) : (
                          <KeyboardArrowRighIcon
                            sx={{ color: colors.primary }}
                          />
                        ))}
                    </Box>

                    {(() => {
                      const isExpanded =
                        expandedStates[`${cardId}-warnings`] === true;
                      return (
                        <Collapse in={isExpanded}>
                          {uniquePosAssessments.length > 0 ? (
                            <List dense disablePadding>
                              {uniquePosAssessments.map((item, i) => {
                                return (
                                  <ListItem
                                    key={i}
                                    sx={{
                                      cursor: 'pointer',
                                      transition: 'background-color 0.2s ease',
                                      '&:hover': {
                                        textDecoration: 'underline',
                                      },
                                    }}
                                    onClick={() => {
                                      console.log('Clicked item:', { item });

                                      // Get tab mapping for this item
                                      const tabMapping = getTabMapping(item);
                                      console.log('Tab mapping:', tabMapping);

                                      if (tabMapping && onNavigateToTab) {
                                        console.log(
                                          `Switching to tab ${tabMapping.tabIndex}...`
                                        );
                                        onNavigateToTab(tabMapping.tabIndex);

                                        // If there's a specific element ID, scroll to it after tab switch
                                        if (tabMapping.elementId) {
                                          // need this function to ensure DOM updates before scrolling to the element
                                          requestAnimationFrame(() => {
                                            const element =
                                              document.getElementById(
                                                tabMapping.elementId
                                              );
                                            console.log(
                                              'After tab switch, looking for element:',
                                              element
                                            );

                                            if (element) {
                                              element.scrollIntoView({
                                                behavior: 'smooth',
                                                block: 'center',
                                              });
                                              console.log(
                                                'Scrolled to tab element'
                                              );
                                            }
                                          });
                                        }
                                      }
                                    }}
                                  >
                                    <ListItemText
                                      primary={
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 1,
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              width: '4px',
                                              height: '4px',
                                              borderRadius: '50%',
                                              backgroundColor: colors.primary,
                                              mt: '8px',
                                              ml: '2px',
                                              flexShrink: 0,
                                            }}
                                          />
                                          <Typography
                                            sx={{
                                              marginLeft: '4px',
                                              fontSize:
                                                typography.fontSizes.small,
                                              letterSpacing: '0.25px',
                                            }}
                                          >
                                            <EditableAssessmentText item={item} scoreType={assessment.score_type} listType={item.result_sign === 'Positive' ? 'pos' : 'neg'} riskSummary={risk_summary} onUpdateRiskSummary={onUpdateRiskSummary} />
                                          </Typography>
                                        </Box>
                                      }
                                      sx={{ ml: 0, my: 0 }}
                                    />
                                  </ListItem>
                                );
                              })}
                            </List>
                          ) : (
                            <div />
                          )}
                        </Collapse>
                      );
                    })()}
                  </Box>

                  {/* Risk Mitigators */}
                  <Box sx={{ mb: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 1,
                        cursor:
                          uniqueNegAssessments.length > 0
                            ? 'pointer'
                            : 'default',
                        '&:hover':
                          uniqueNegAssessments.length > 0
                            ? {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            }
                            : {},
                        p: 1,
                        borderRadius: 1,
                        gap: 1,
                      }}
                      onClick={
                        uniqueNegAssessments.length > 0
                          ? () => toggleExpanded(cardId, 'mitigators')
                          : undefined
                      }
                    >
                      <SuccessRoundIcon size={16} iconSize={10} />
                      <Typography
                        sx={{
                          color: colors.primary,
                          fontWeight: typography.fontWeights.regular,
                          fontSize: typography.fontSizes.medium,
                          textTransform: 'none',
                          letterSpacing: '0.5px',
                        }}
                      >
                        {`${uniqueNegAssessments.length} Risk ${uniqueNegAssessments.length === 1
                          ? 'mitigator'
                          : 'mitigators'
                          }`}
                      </Typography>
                      <Box sx={{ flex: 1 }} />

                      {uniqueNegAssessments.length > 0 &&
                        (expandedStates[`${cardId}-mitigators`] ? (
                          <KeyboardArrowDownIcon
                            fontSize="medium"
                            sx={{ color: colors.primary }}
                          />
                        ) : (
                          <KeyboardArrowRighIcon
                            sx={{ color: colors.primary }}
                          />
                        ))}
                    </Box>

                    {(() => {
                      const isExpanded =
                        expandedStates[`${cardId}-mitigators`] === true;
                      return (
                        <Collapse in={isExpanded}>
                          {uniqueNegAssessments.length > 0 ? (
                            <List dense disablePadding>
                              {uniqueNegAssessments.map((item, i) => {
                                return (
                                  <ListItem
                                    key={i}
                                    sx={{
                                      cursor: 'pointer',
                                      transition: 'background-color 0.2s ease',
                                      '&:hover': {
                                        textDecoration: 'underline',
                                      },
                                    }}
                                    onClick={() => {
                                      console.log('Clicked mitigator item:', {
                                        item,
                                      });

                                      // Get tab mapping for this item
                                      const tabMapping = getTabMapping(item);
                                      console.log(
                                        'Tab mapping for mitigator:',
                                        tabMapping
                                      );

                                      if (tabMapping && onNavigateToTab) {
                                        console.log(
                                          `Switching to tab ${tabMapping.tabIndex} for mitigator...`
                                        );
                                        onNavigateToTab(tabMapping.tabIndex);

                                        // If there's a specific element ID, scroll to it after tab switch
                                        if (tabMapping.elementId) {
                                          // need this function to ensure DOM updates before scrolling to the element
                                          requestAnimationFrame(() => {
                                            const element =
                                              document.getElementById(
                                                tabMapping.elementId
                                              );
                                            console.log(
                                              'After tab switch, looking for mitigator element:',
                                              element
                                            );

                                            if (element) {
                                              element.scrollIntoView({
                                                behavior: 'smooth',
                                                block: 'center',
                                              });
                                              console.log(
                                                'Scrolled to tab element for mitigator'
                                              );
                                            }
                                          });
                                        }
                                      }
                                    }}
                                  >
                                    <ListItemText
                                      primary={
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 1,
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              width: '4px',
                                              height: '4px',
                                              borderRadius: '50%',
                                              backgroundColor: colors.primary,
                                              mt: '8px',
                                              ml: '2px',
                                              flexShrink: 0,
                                            }}
                                          />
                                          <Typography
                                            sx={{
                                              marginLeft: '4px',
                                              fontSize:
                                                typography.fontSizes.small,
                                              letterSpacing: '0.25px',
                                            }}
                                          >
                                            <EditableAssessmentText item={item} scoreType={assessment.score_type} listType={item.result_sign === 'Positive' ? 'pos' : 'neg'} riskSummary={risk_summary} onUpdateRiskSummary={onUpdateRiskSummary} />
                                          </Typography>
                                        </Box>
                                      }
                                      sx={{ ml: 0, my: 0 }}
                                    />
                                  </ListItem>
                                );
                              })}
                            </List>
                          ) : (
                            <div />
                          )}
                        </Collapse>
                      );
                    })()}
                  </Box>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Paper>
  );
};

export default RiskSummaryDetailView;
